import { Injectable } from "@angular/core";
import {
  RatePromiseService,
  SystemConfigPromiseService,
  OrderDataPromiseLookupService,
  OrdersTableDefinitionFactory,
  ProfilePromiseActionService,
  ProfileSettingKeys,
  RateCache,
  ActiveUserProfilePromiseService,
} from "shared/cubiq-services";
import { BfxBlotterOrdersService } from "../bfx-blotter-orders/bfx-blotter-orders.service";
import { BfxBlotterInboxFilter } from "./bfx-blotter-inbox-filter";
import { GoldenLayoutService } from "../golden-layout/golden-layout-service";
import { BlotterType, AssetClass } from "shared/cubiq-model-multi-asset";
import { Subject, Subscription } from "rxjs";
import { BfxUserProfileService } from "../bfx-common/services/bfx-user-profile.service";
import { BfxBlotterFilterService } from "../bfx-blotter-filter/bfx-blotter-filter.service";
import {
  FilterType,
  BfxBlotterFilterId,
  BfxBlotterFilter,
} from "../bfx-blotter-filter/bfx-blotter-filter.constants";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { COLUMN_ORDER_ID } from "shared/cubiq-services/model/OrdersTableDefinitionFactory.constants";
import {
  GLComponentAlertStatus,
  GoldenLayoutServiceProfileConfig,
} from "../golden-layout/golden-layout-component";
import { LimitOrder } from "shared/cubiq-services/model/orders/LimitOrder";
import { OrdersTableMode } from "shared/cubiq-services/model/OrdersTableMode";
import { throttleTime } from "rxjs/operators";

const INBOX_AUTO_OPEN_INTERVAL = 300;

@Injectable()
export class BfxBlotterInboxService
  extends BfxBlotterOrdersService
  implements GoldenLayoutServiceProfileConfig
{
  private inboxOrders: any = {};

  private filterConfiguration = [];
  private filters: Array<BfxBlotterFilter> = [];
  private selectedFilters: Array<BfxBlotterFilter> = [];

  private component: any = null;
  private readonly profileKey = `${ProfileSettingKeys.userProfileDefaultBlotter}${
    BlotterType[BlotterType.ORDER]
  }/INBOX/SERVICE`;

  private _mode: OrdersTableMode = OrdersTableMode.BASE;
  private _modeConfig: boolean = false;

  public get mode() {
    return this._mode;
  }

  public set mode(value: OrdersTableMode) {
    this._modeConfig = true;

    if (value !== this._mode) {
      this._mode = value;
      // re-apply
      this.updateFilterData();
    }
  }

  public get hasMode(): boolean {
    // service mode of last open inbox is prefered over golden layout component
    // this is here to prevent initalization value of this._mode clobbering
    // saved profile in the absense of a service configuration -dc

    return this._modeConfig;
  }

  public get isDealtMode() {
    return OrdersTableMode.DEALT === this.mode || OrdersTableMode.NDF_DEALT === this.mode;
  }

  public subjectNewItems: Subject<void> = new Subject<any>();
  public subjectUpdatedItems: Subject<string[]> = new Subject<any>();

  public openInboxSubscription: Subscription;

  constructor(
    protected rateService: RatePromiseService,
    rateCache: RateCache,
    protected systemConfigService: SystemConfigPromiseService,
    protected orderService: OrderDataPromiseLookupService,
    protected inboxFilter: BfxBlotterInboxFilter,
    public tableDefinitionFactory: OrdersTableDefinitionFactory,
    protected blotterOrdersService: BfxBlotterOrdersService,
    protected goldenLayoutService: GoldenLayoutService,
    protected filterService: BfxBlotterFilterService,
    profileActionService: ProfilePromiseActionService,
    protected userProfileService: BfxUserProfileService,
    activeUserService: ActiveUserProfilePromiseService,
  ) {
    super(
      rateService,
      rateCache,
      systemConfigService,
      orderService,
      tableDefinitionFactory,
      profileActionService,
      activeUserService,
    );
    this.filterService.modelUpdateSubject.subscribe(this.primeFilters.bind(this));
    this.subscribeToSubject();
    this.inboxFilter.updateFilterDataCallback = this.updateFilterData.bind(this);

    // reduce opening inbox to a frequency on a human scale
    // to reduce pressure on golden-layout/openfin logic during large acceptance bursts
    this.systemConfigService.canViewInboxBlotter().then((canSeeIt) => {
      if (canSeeIt) {
        this.openInboxSubscription = this.subjectNewItems
          .pipe(throttleTime(INBOX_AUTO_OPEN_INTERVAL))
          .subscribe(() => {
            this.openInboxBlotter();
          });
      }
    });
  }

  protected primeFilters(): Promise<void> {
    return new Promise<void>((res) => {
      this.filterService
        .getItems(
          FilterType.BlotterFilter,
          new BfxBlotterFilterId(BlotterType.ORDER, AssetClass.FIX),
        )
        .then((filters: Array<BfxBlotterFilter>) => {
          this.filters = filters;
          res();
        });
    });
  }

  protected initData(primed: boolean): void {
    this.primeFilters().then((res) => {
      super.initData(primed);
    });
  }

  public updateFilterData(): void {
    this.inboxOrders = {};

    Object.keys(this._rawData).forEach((key: string) => {
      let orderId: string = key;
      let value: LimitOrder = this._rawData[key];
      if (this.inboxFilter.doesFilterPass(value, this.isDealtMode)) {
        this.inboxOrders[orderId] = true;
      }
    });

    if (this.component != null) {
      this.updateBadgeCount(this.component);
    }
  }

  protected onFullUpdateEvent(rows: Array<any>): void {
    super.onFullUpdateEvent(rows);

    let hasNewRows: boolean = false;

    let updatedItemIds: Array<string> = [];

    rows.forEach((row: any) => {
      let orderId: string = row[this._tableDefinition.primaryKey];
      let wasInbox: boolean = this.inboxOrders[orderId];
      let isInbox: boolean = this.inboxFilter.doesFilterPass(row, this.isDealtMode);

      if (!wasInbox && isInbox) {
        hasNewRows = true;
      }

      if (wasInbox) {
        updatedItemIds.push(orderId);
      }

      if (isInbox) {
        this.inboxOrders[orderId] = true;
      } else if (!isInbox) {
        delete this.inboxOrders[orderId];
      }
    });

    if (updatedItemIds.length > 0) {
      this.subjectUpdatedItems.next(updatedItemIds);
    }

    if (hasNewRows) {
      this.subjectNewItems.next();
    }
  }

  protected onDeleteEvent(orderId: string): void {
    super.onDeleteEvent(orderId);
    delete this.inboxOrders[orderId];
  }

  public updateFilterSelection(filter: Array<string>): void {
    this.filterConfiguration = filter;

    this.selectedFilters = this.filters.filter(
      (filter: BfxBlotterFilter) => this.filterConfiguration.indexOf(filter.filterId) >= 0,
    );
    this.inboxFilter.setSelectedFilters(this.selectedFilters);
    this.updateFilterData();
  }

  protected openInboxBlotter(): void {
    this.goldenLayoutService.showInbox(true);
  }

  public getSelectedFilters(): Array<BfxBlotterFilter> {
    return this.selectedFilters;
  }

  public getItemCount(): number {
    return Object.keys(this.inboxOrders).length;
  }

  public updateBadgeCount(component: any): void {
    this._ready.then(() => {
      if (this.component == null && component != null) {
        this.component = component;
        this.updateFilterData();
        return;
      }
      let badgeCount = this.getItemCount();
      let alertStatus = this.component.getAlertStatus();
      this.goldenLayoutService.updateBadge(component, badgeCount, alertStatus);
    });
  }

  public getFilterConfiguration(): any {
    return this.filterConfiguration;
  }

  public glServiceProfileConfig(): GoldenLayoutServiceProfileConfig {
    return this;
  }

  public getServiceConfiguration(): any {
    let config: any = {};

    if (this._modeConfig) {
      config.mode = this._mode;
    }

    if (this.selectedFilters != null) {
      config.selectedFilters = this.selectedFilters.map(
        (filter: BfxBlotterFilter) => filter.filterId,
      );
    }

    return config;
  }

  public setServiceConfiguration(config: any): void {
    if (typeof config == "object" && config != null) {
      if (config.hasOwnProperty("mode") && OrdersTableMode.getAllElements().includes(config.mode)) {
        this.mode = config.mode;
      }

      if (config.selectedFilters != null && Array.isArray(config.selectedFilters)) {
        this.updateFilterSelection(config.selectedFilters);
      }
    }
  }

  protected acceptsRow(row: any): boolean {
    return true;
  }
}
